import { getCookie, removeCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import createShipTo from "@insite/client-framework/Store/Pages/Addresses/Handlers/CreateShipTo";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsPreloadingData";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShippingMethod";
import updateBillTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateBillTo";
import updateShipTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateShipTo";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import ShippingAddress, {
    ShippingAddressStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/ShippingAddress";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import { COOKIE_KEYS } from "@MasterBluePrint/Common/Utilities/CookieKeys";
import ShippingTermsConsentModal from "@MasterBluePrint/Components/ShippingTermsConsentModal";
import { Cart } from "@MasterBluePrint/Overrides/Services/CartService";
import { AccountPreferenceService as ap } from "@MasterBluePrint/Services/AccountPreferenceService";
import { AccountPreferenceApiParameter } from "@MasterBluePrint/Services/AccountPreferenceService";
import themeColors, { themeFontSizes, themeFontWeights } from "@MasterBluePrint/Styles/variables";
import React, { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    collectNumberData: any;
    shipToAddressFieldsData?: any;
    collectNumberErrorData?: any;
    updateShippingComponentState?: any;
    isShippingTermsEnabled?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cartData = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const shipTosDataView = cartData
        ? getShipTosDataView(state, { billToId: cartData.billToId, expand: ["validation"], exclude: ["showAll"] })
        : undefined;
    let newAddress;
    let oneTimeAddress;
    let isShipToSameAsBillTo = false;
    let canUseBillToAsShipTo = false;
    if (cartData && cartData.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        newAddress = shipTos!.find(s => s.isNew && !s.oneTimeAddress);
        oneTimeAddress = shipTos!.find(s => s.oneTimeAddress);
        isShipToSameAsBillTo = cartData.billToId === cartData.shipToId;
        canUseBillToAsShipTo = shipTos!.some(shipTo => shipTo.id === cartData.billToId);
    }

    return {
        cart: cartState.value,
        showShippingMethod: session.fulfillmentMethod === FulfillmentMethod.Ship,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            session.fulfillmentMethod === FulfillmentMethod.PickUp,
        session: state.context.session,
        accountSettings: settingsCollection.accountSettings,
        cartSettings: settingsCollection.cartSettings,
        shipToState: getShipToState(state, cartData ? cartData.shipToId : undefined),
        shipTosDataView,
        cartData,
        countries: getCurrentCountries(state),
        cartPageLink: getPageLinkByPageType(state, "CartPage")?.url,
        billToState: getBillToState(state, cartData?.billToId),
        canUseBillToAsShipTo,
        isShipToSameAsBillTo,
        newAddress,
        oneTimeAddress,
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setIsPreloadingData,
    updateShipTo,
    setCurrentShipTo,
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    loadShipTo,
    loadShipTos,
    updateBillTo,
    createShipTo
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutShippingCarrierServiceStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    carrierGridItem?: GridItemProps;
    carrierSelect?: SelectPresentationProps;
    serviceGridItem?: GridItemProps;
    serviceSelect?: SelectPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    shippingAddress?: ShippingAddressStyles;
    collectNumberText?: TextFieldPresentationProps;
    collectGridItem?: GridItemProps;
    parentContainer?: InjectableCss;
    carrierWrapper?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
}

interface AccountPreferencesResponse {
    accountPreference: null | {
        userProfileId: string,
        preferenceKey: string,
        value: string,
        customerId: string,
    };
    carriers: null;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    parentContainer: {
        css: css`
            margin-bottom: 1.875rem;

            h2.section-heading,
            h3.section-heading {
                color: ${themeColors.colorPrimary};
                font-family: "Roboto", sans-serif;
                font-size: ${themeFontSizes.size20};
                font-weight: ${themeFontWeights.fontWeightForBold};
                letter-spacing: 0;
                line-height: 1.375rem;
                margin-bottom: 1.25rem;

                &:before {
                    display: inline-block;
                    border: 0.0625rem solid ${themeColors.colorPrimary};
                    border-radius: 100%;
                    content: attr(data-number);
                    height: 1.5625rem;
                    width: 1.5625rem;
                    background-color: ${themeColors.colorPrimary};
                    color: ${themeColors.colorWhite};
                    font-family: "Roboto", sans-serif;
                    font-size: ${themeFontSizes.size16};
                    font-weight: ${themeFontWeights.fontWeightForBold};
                    letter-spacing: 0;
                    line-height: 1.5rem;
                    margin-right: 0.9375rem;
                    text-align: center;
                    vertical-align: middle;
                }
            }
        `,
    },
    carrierWrapper: {
        width: [12, 12, 6, 6, 6],
        css: css`
            max-width: 50%;
            flex-basis: 50%;
            flex-direction: column;

            @media (min-width: 991px) {
                padding-top: 0;
            }

            @media (min-width: 767px) {
                padding-bottom: 0;

                > div {
                    padding-bottom: 0;
                }
            }

            @media (max-width: 990px) {
                padding-right: 0;
            }

            @media (max-width: 767px) {
                padding-left: 0;
                padding-right: 0;
                padding-top: 0.9375rem;
                border-top: 0.0625rem solid ${themeColors.colorBorderBrands};
            }
        `,
    },
    container: {
        gap: 20,
        css: css`
            padding: 1.875rem 2.8125rem;
            background: ${themeColors.colorWhite};
            box-shadow: 0 0.125rem 0.250625rem 0.250625rem rgb(0 0 0 / 3%);
            border-radius: 0.125rem;
            > div {
                margin: 0;
                width: 100% !important;
            }

            label {
                color: ${themeColors.colorHeadText};
                font-family: "Roboto", sans-serif;
                font-size: ${themeFontSizes.size18};
                font-weight: ${themeFontWeights.fontWeightMedium};
                letter-spacing: 0;
                line-height: 1.4375rem;
            }

            input:not(.react-datetime-picker__inputGroup__input),
            select:not(.react-datetime-picker__inputGroup__input),
            textarea {
                min-width: 17.4375rem;
            }
            &.shipping-wrapper {
                select:not(.react-datetime-picker__inputGroup__input) {
                    font-size: ${themeFontSizes.size16} !important;
                    font-weight: ${themeFontWeights.fontWeightForBold};
                    color: ${themeColors.colorAddress} !important;
                    > option {
                        color: ${themeColors.colorAddress};
                    }
                }
                input:not(.react-datetime-picker__inputGroup__input) {
                    font-size: ${themeFontSizes.size16} !important;
                    font-weight: ${themeFontWeights.fontWeightForBold};
                    color: ${themeColors.colorAddress} !important;
                }
            }

            @media (max-width: 767px) {
                padding: 1.3125rem 0.9375rem 1.25rem 0.8125rem;
            }
            @media screen and (min-width: 991px) and (max-width: 1100px) {
                input:not(.react-datetime-picker__inputGroup__input),
                select:not(.react-datetime-picker__inputGroup__input),
                textarea {
                    min-width: 15.5625rem;
                }
            }
        `,
    },
    shippingAddressGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding: 0;
            @media (max-width: 990px) {
                margin-bottom: 0;
                padding-bottom: 0.9375rem;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            border-right: 0.0625rem solid ${themeColors.colorBorderBrands};
                        `,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 28.125rem;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    carrierGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            padding-left: 0.9375rem;
            padding-top: 0;

            @media (max-width: 767px) {
                padding-left: 0;
            }
            @media (min-width: 768px) {
                padding-left: 1.25rem;
            }
        `,
    },
    serviceGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            padding-left: 0.9375rem;

            @media (max-width: 767px) {
                padding-left: 0;
            }
            @media (min-width: 768px) {
                padding-left: 1.25rem;
            }
        `,
    },
    deliveryDateGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            padding-left: 0.9375rem;

            @media (min-width: 768px) {
                padding-left: 1.25rem;
            }
        `,
    },
    pickUpDateGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            padding-left: 0.9375rem;

            @media (min-width: 768px) {
                padding-left: 1.25rem;
            }
        `,
    },
    collectGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            padding-left: 0.9375rem;

            @media (max-width: 767px) {
                padding-left: 0;
            }
            @media (min-width: 768px) {
                padding-left: 1.25rem;
            }

            label[class^="TypographyStyle"],
            label[class*="TypographyStyle"] {
                span {
                    padding-left: 0.9375rem;
                    &:first-child {
                        padding-left: 0;
                    }
                }
            }
        `,
    },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

// ToDo - This component code is executed multiple times and this might lead to issues in future. This need to be addressed
const CheckoutReviewAndSubmitCarrierService: FC<Props> = ({
    cart,
    cartSettings,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    showShippingMethod,
    setShippingMethod,
    showPickUpDate,
    setIsPreloadingData,
    shipToState,
    shipTosDataView,
    countries,
    session,
    history,
    cartPageLink,
    billToState,
    collectNumberData,
    updateShipTo,
    shipToAddressFieldsData,
    updateShippingComponentState,
    loadShipTo,
    loadShipTos,
    canUseBillToAsShipTo,
    isShipToSameAsBillTo,
    newAddress,
    oneTimeAddress,
    collectNumberErrorData,
    createShipTo,
    isShippingTermsEnabled
}) => {
    const { fulfillmentMethod } = session;
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [collectNumber, setCollectNumber] = useState("");
    const [collectNumberError, setCollectNumberError] = useState("");
    const collectNumberRef = useRef<HTMLInputElement>(null);
    const [modalOpen, setModalOpen] = useState(false);


    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="checkoutShipping_carrierServiceLoading" />
            </StyledWrapper>
        );
    }

    const carrierChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedCarrier = cart.carriers!.find(c => c.id === event.currentTarget.value)!;
        const defaultShipVias = selectedCarrier.shipVias!.filter(o => o.isDefault);
        const shipVia = defaultShipVias.length > 0 ? defaultShipVias[0] : selectedCarrier.shipVias![0];
        setPreferedCarrier(null);
        setpreferedShipVia(null);
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedCarrier(selectedCarrier);
        setSelectedShipVia(shipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };
    const shipViaChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (!selectedCarrier) {
            return;
        }
        setPreferedCarrier(null);
        setpreferedShipVia(null);
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedShipVia(selectedShipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };
    const useBillingAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setCurrentShipTo({ shipToId: cart.billToId! });
    };

    const changeShippingAddressHandler = (address: ShipToModel) => {
        if (address.isNew && address.isDefault) {
            updateShipTo({
                billToId: billToId!,
                shipTo: address,
                isEdit: false,
                onSuccess: hasCartlines => {
                    if (!hasCartlines) {
                        history.push(cartPageLink!);
                    }
                },
            });
        } else if (address.isNew) {
            createShipTo({
                billToId: billToId!,
                shipTo: {
                    ...address,
                },
                onComplete: result => {
                    if (result.apiResult && address.isDefault) {
                        setCurrentShipTo({ shipToId: result.apiResult.id });
                    }
                },
            });
        } else {
            updateShipTo({
                billToId: billToId!,
                shipTo: address,
                isEdit: true,
                onSuccess: hasCartlines => {
                    if (!hasCartlines) {
                        history.push(cartPageLink!);
                    }
                },
            });
        }
    };

    const validateCollectNumber = (collectNumber: string) => {
        collectNumberData(collectNumber);
        return true;
    };

    const { billToId } = cart;

    const checkForClassifiedProduct = (cart : Cart) => {
        if (cart?.cartLines) {
            for (const cartLine of cart.cartLines) {
                if (cartLine?.properties?.shippingClassification === "MP") {
                    return true;
                }
            }
        }
        return false;
    };

    const checkForUSAddress = (address: Readonly<ShipToModel> | undefined) => {
        return (address?.country?.name?.toLowerCase() === "united states" || address?.country?.abbreviation === "US");
    }
    const parameter: AccountPreferenceApiParameter = {
        UserProfileId: cart.billToId ? cart.billToId : ''
        , PreferenceKey: 'CarrierAndService'
        , Value: ''
        , CustomerId: ''
    };

    const featureFlags: string[] = ["TEGE-196"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const enablePreferredShippingCarrierAndService = flagValues["TEGE-196"];

    const [preferedCarrier, setPreferedCarrier] = useState<CarrierDto | null>(null);
    const [preferedShipVia, setpreferedShipVia] = useState<ShipViaDto | null>(null);
    const [preferedShipViaId, setpreferedShipViaId] = useState("");
    const [showCollectNumber, setShowCollectNumber] = useState(false);
    useEffect(() => {
        if (enablePreferredShippingCarrierAndService) {
            if (!cart.billToId) { return; }
            ap.getAccountPreferences(parameter).then((apiResponse: { response: AccountPreferencesResponse }) => {
                const { accountPreference } = apiResponse.response;
                if (accountPreference) {
                    const carrierAndService = JSON.parse(accountPreference.value);
                    setpreferedShipViaId(carrierAndService.shipViaId);
                    const preferredCarrier = cart.carriers!.find((c: CarrierDto) => c.id === carrierAndService.carrierId);
                    setPreferedCarrier(preferredCarrier || null);
                    setShowCollectNumber(!!(preferredCarrier && preferredCarrier.description.toLowerCase().includes("collect")));
                }
            });
        }
    }, [cart?.billToId, enablePreferredShippingCarrierAndService]);

    useEffect(() => {
        setSelectedCarrier(preferedCarrier);
        if (selectedCarrier) {
            setpreferedShipVia(preferedCarrier?.shipVias!.find((s: ShipViaDto) => s.id === preferedShipViaId) || null);
        }
    }, [preferedCarrier]);

    useEffect(() => {
        if (preferedShipVia) {
            setSelectedShipVia(preferedShipVia);
        }
    }, [preferedShipVia]);
    

    useEffect(() => {
        setCollectNumberError(collectNumberErrorData);
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] });
        }
    }, [collectNumberErrorData, cart?.billToId]);

    useEffect(() => {
        if (!shipToState.value && cart) {
            loadShipTo({ billToId: cart.billToId!, shipToId: cart.shipToId! });
        }
    }, [cart?.billToId, cart?.shipToId]);

    useEffect(() => {
        if (cart) {
            setSelectedCarrier(preferedCarrier || cart.carrier);
            setSelectedShipVia(preferedShipVia || cart.shipVia);
            if (selectedCarrier?.description.toLowerCase().includes("collect")) {
                setCollectNumber(cart.properties["customerFreightBillingAcctNumber"]);
                validateCollectNumber(cart.properties["customerFreightBillingAcctNumber"]);    
            }
        }
        if (preferedCarrier) {
            setShowCollectNumber(!!preferedCarrier?.description.toLowerCase().includes("collect"));
        }
        else {
            setShowCollectNumber(!!cart?.carrier?.description.toLowerCase().includes("collect"));
        }
    }, [cart?.carrier, cart?.shipVia]);

    useEffect(() => {     
        if (isShippingTermsEnabled) {
            const isClassifiedProduct = checkForClassifiedProduct(cart);
            const isAddressUS = checkForUSAddress(shipToState?.value);
            const shippingTermsAccepted = getCookie(COOKIE_KEYS.COOKIE_SHIPPING_TERMS_ACCEPTED);

            // Remove terms cookie only if user did not approve shipping terms or product is gone or address is now US
            if ((!isClassifiedProduct || isAddressUS) && shippingTermsAccepted === 'false') {
                removeCookie(COOKIE_KEYS.COOKIE_SHIPPING_TERMS_ACCEPTED);
            }

            if (isClassifiedProduct && !isAddressUS) {
                const hasShownPopup = getCookie(COOKIE_KEYS.COOKIE_SHIPPING_TERMS_POPUP_SHOWN) === "true";

                // Set default 'false' if no terms cookie yet
                if (shippingTermsAccepted === undefined) {
                    setCookie(COOKIE_KEYS.COOKIE_SHIPPING_TERMS_ACCEPTED, "false");
                }

                // Show popup once per session
                if (!hasShownPopup) {
                    setCookie(COOKIE_KEYS.COOKIE_SHIPPING_TERMS_POPUP_SHOWN, "true");
                    setModalOpen(true);
                }
            }   
        }
    },[cart?.cartLines, shipToState?.value, isShippingTermsEnabled]);

    useEffect(() => {
        if (!countries || !shipToState.value || !billToState.value) {
            updateShippingComponentState(true);
        } else {
            updateShippingComponentState(false);
        }
    }, [countries, shipToState.value, billToState.value, updateShippingComponentState]);

    const handleClose = () => {
        setModalOpen(false);
    }

    if (!countries || !shipToState.value || !billToState.value) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.parentContainer}>
                <h2 className="section-heading">{translate("Shipping")}</h2>
                <GridContainer {...styles.container} className="shipping-wrapper">
                    {fulfillmentMethod === FulfillmentMethod.Ship && shipToAddressFieldsData && (
                        <GridItem {...styles.shippingAddressGridItem} className="custom-shipping-Address">
                            <ShippingAddress
                                address={shipToState.value}
                                currentBillToId={cart.billToId}
                                countries={countries}
                                addressFieldDisplayCollection={shipToAddressFieldsData}
                                onChange={changeShippingAddressHandler}
                                showUseBillingAddress={canUseBillToAsShipTo}
                                isUseBillingAddressDisabled={isShipToSameAsBillTo}
                                onClickUseBillingAddress={useBillingAddressHandler}
                                extendedStyles={styles.shippingAddress}
                                newAddress={newAddress}
                                oneTimeAddress={oneTimeAddress}
                            ></ShippingAddress>
                        </GridItem>
                    )}

                    <GridItem {...styles.carrierWrapper} className="carrier-wrapper">
                        {showShippingMethod && (
                            <>
                                {cart.carriers!.length === 0 && (
                                    <GridItem {...styles.noCarriersFoundGridItem}>
                                        <Typography {...styles.noCarriersFoundText}>
                                            {siteMessage("ReviewAndPay_NoCarriersFound")}
                                        </Typography>
                                    </GridItem>
                                )}
                                {cart.carriers!.length > 0 && (
                                    <>
                                        <GridItem {...styles.carrierGridItem}>
                                            <Select
                                                label={translate("Select Carrier")}
                                                {...styles.carrierSelect}
                                                value={selectedCarrier?.id || ""}
                                                onChange={carrierChangeHandler}
                                                data-test-selector="checkoutReviewAndSubmit_carrierSelect"
                                                name="carrier"
                                            >
                                                {cart.carriers!.map(c => {
                                                    const id = c.id!.toString();
                                                    return (
                                                        <option key={id} value={id}>
                                                            {c.description}
                                                        </option>
                                                    );
                                                })}
                                            </Select>
                                        </GridItem>
                                        <GridItem {...styles.serviceGridItem}>
                                            {cart?.carrier && (
                                                <Select
                                                    label={translate("Select Service")}
                                                    {...styles.serviceSelect}
                                                    value={selectedShipVia?.id || ""}
                                                    onChange={shipViaChangeHandler}
                                                    data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                                >
                                                    {selectedCarrier &&
                                                        selectedCarrier.shipVias!.map(s => {
                                                            const id = s.id.toString();
                                                            return (
                                                                <option key={id} value={id}>
                                                                    {s.description}
                                                                </option>
                                                            );
                                                        })}
                                                </Select>
                                            )}
                                        </GridItem>
                                    </>
                                )}
                                {cart?.carrier && showCollectNumber && (
                                    <GridItem {...styles.collectGridItem}>
                                        {/* <CollectNumber selectedCarrier={selectedCarrier} /> */}
                                        <TextField
                                            {...styles.collectNumberText}
                                            label={
                                                <>
                                                    <span aria-hidden>{translate("Collect Number")}</span>
                                                    <VisuallyHidden>{translate("Collect Number")}</VisuallyHidden>
                                                </>
                                            }
                                            value={collectNumber}
                                            onChange={e => setCollectNumber(e.currentTarget.value)}
                                            onBlur={e => validateCollectNumber(e.currentTarget.value)}
                                            onKeyUp={e => validateCollectNumber(e.currentTarget.value)}
                                            required
                                            error={collectNumberError}
                                            data-test-selector="checkoutReviewAndSubmit_collectNumber"
                                            ref={collectNumberRef}
                                            min={6}
                                            max={9}
                                        />
                                    </GridItem>
                                )}

                                {cartSettings.canRequestDeliveryDate && (
                                    <GridItem
                                        {...styles.deliveryDateGridItem}
                                        data-test-selector="checkoutShippingRequestedDeliveryDate"
                                    >
                                        <DatePicker
                                            label={`${translate("Request Delivery Date")} (${translate("optional")})`}
                                            hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                            {...styles.deliveryDatePicker}
                                            selectedDay={cart.requestedDeliveryDateDisplay!}
                                            dateTimePickerProps={{
                                                minDate: new Date(),
                                                maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                                monthAriaLabel: translate("Request delivery date month"),
                                                dayAriaLabel: translate("Request delivery date day"),
                                                yearAriaLabel: translate("Request delivery date year"),
                                                ...styles.deliveryDatePicker?.dateTimePickerProps,
                                            }}
                                            onDayChange={handleRequestDeliveryDateChanged}
                                        />
                                    </GridItem>
                                )}
                            </>
                        )}
                    </GridItem>
                    {showPickUpDate && (
                        <>
                            <GridItem
                                {...styles.pickUpDateGridItem}
                                data-test-selector="checkoutPickUpRequestedDeliveryDate"
                            >
                                <DatePicker
                                    label={`${translate("Request Pick Up Date")} (${translate("optional")})`}
                                    hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                                    {...styles.pickUpDatePicker}
                                    selectedDay={cart.requestedPickupDateDisplay!}
                                    dateTimePickerProps={{
                                        minDate: new Date(),
                                        maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                        ...styles.pickUpDatePicker?.dateTimePickerProps,
                                    }}
                                    onDayChange={handleRequestPickUpDateChanged}
                                    monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                        "Enter month, format M M",
                                    )}`}
                                    dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                        "Enter day, format D D",
                                    )}`}
                                    yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                        "Enter year, format Y Y Y Y",
                                    )}`}
                                />
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </StyledWrapper>
        <ShippingTermsConsentModal 
            onClose={handleClose}
            isModalOpen={modalOpen}
        />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutReviewAndSubmitCarrierService));
